import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subscribeNotificationInsert } from "@/lib/notificationsBus";

/**
 * Broker-facing: tracks unread `quote_request_received` notifications.
 * Toast + chime are owned by useGlobalNotificationAlerts.
 */
export function useUnreadQuoteRequests(userId: string | null | undefined) {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!userId) { setCount(0); return; }
    const { count: c } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("recipient_user_id", userId)
      .eq("type", "quote_request_received")
      .eq("is_read", false);
    setCount(c ?? 0);
  }, [userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("recipient_user_id", userId)
      .eq("type", "quote_request_received")
      .eq("is_read", false);
    setCount(0);
  }, [userId]);

  useEffect(() => {
    if (!userId) { setCount(0); return; }
    fetchCount();

    const off = subscribeNotificationInsert(userId, (row) => {
      if ((row as { type?: string })?.type !== "quote_request_received") return;
      setCount((c) => c + 1);
    });
    return off;
  }, [userId, fetchCount]);

  return { count, markAllRead };
}
